import { isEqual } from 'lodash';
import * as React from 'react';
import { IABExperiment } from '@cian/api-models/monolith-python/cian-api/site/v1/get-ab-experiments';
import { IButtonsAPI, ICommentsAPI } from '@cian/api-models/search-card-fragment/v1/get-offer-card.props';
import { EMaxAuctionService } from '../../../repositories/search-offers/v2/search-offers-desktop.data';
import { ILogger } from '@cian/logger';

import { TAgentRating } from '../../../api/agent';
import { ICommercialTrackingOfferParams, ITrackingData, IUserGADataLayerData, TUser } from '../../../common/state';
import { IJsonQuery } from '../../../json_query';
import { IBreadcrumb, IOffer, ISerpDataQueristringToUri } from '../../../offer/types';
import { getPriceRangeForPuidAdFox } from '../../../utils/adfox';
import { isCommercial, offerTypeFromJsonQuery } from '../../../utils/category';
import { IMakeRequest } from '../../../utils/request';
import { getOfferCardCreator, getOfferPrice, integrateAd, IOfferCardCreatorProps } from './helpers';
import { IHideOffer, IHidingOfferInfo } from '../../state/hide_offer';
import { EFeedbackComplaint, IComplaintFeedbackBody } from '../../state/offer_card/feedback_complaint';
import { AuctionStatusNotification } from '../AuctionStatusNotification';
import {
  EPreInfiniteBannerTypes,
  PreInfiniteBanner,
} from '../../../common/components/PreInfiniteBanner/PreInfiniteBanner';
import { EmptyKpSearchContainer } from '../../../common/components/PreInfiniteBanner/EmptyKpSearch/container';
import { IFeatureToggle } from './types';
import { IAbUseExperiment } from '../../../types/abUseExperiments';

const style = require('./index.css');

export interface IOnFavoriteChange {
  (isFavorite: boolean): void;
}

type TCommentsStateProps =
  | 'commentingBlockedOffers'
  | 'commentingBlockedAgents'
  | 'commentingErroneousOffers'
  | 'commentingErroneousAgents';

export interface IOffersStateProps extends Pick<ICommentsAPI, TCommentsStateProps> {
  queryString: string;
  offersQty: number;
  baseUrl: string;
  currentPageNumber: number;
  currentPath: string | undefined;
  currentSubdomain: string;
  experiments: IABExperiment[];
  isBot: boolean;
  isPopupsOpened(): boolean;
  isPrintEnabled: boolean;
  jsonQuery: IJsonQuery;
  logger: ILogger;
  makeRequest: IMakeRequest;
  offers: IOffer[];
  offersPerPage: number;
  qsToUris: ISerpDataQueristringToUri | null;
  ratingBlockedAgents: number[];
  shownId: number | undefined;
  suggestionDistancesSeoText: string | undefined;
  user: TUser;
  userGADataLayerData: IUserGADataLayerData | null;
  favoritesLimitForUnauthUser: number;
  showPushNotification?: boolean;
  hideOffer: IHideOffer;
  isNewAdFox?: boolean;
  breadcrumbs: IBreadcrumb[];
  mlRankingGuid: string | null;
  showEmptyKpSearch?: boolean;

  complaintsFormStatus: EFeedbackComplaint;

  /** Максимальная ставка аукциона на выдаче */
  maxAuctionBet: number;
  /** Максимальная услуга выдачи */
  maxAuctionService?: EMaxAuctionService;
  isLightView?: boolean;
  soprApi: string;
  deviceType: string;
  villagesLink?: string;
  showCountrysideTrap?: boolean;
  aggregatedOffersCount: number;
  extendedOffersCount: number;
  isPromotionsInOffersActive?: boolean;
  isOfferInSavedSearch?: boolean;
  isQaMode?: boolean;
  excludedUtilitiesTermsRegions: number[];
  newbuildingConsultBannerAvailable?: boolean;
  isRedesignEnabled: boolean;
  isCianAgencyExperimentActive?: boolean;
  featureToggle: IFeatureToggle;
  trackingData: ITrackingData;
  auctionBanks?: number[];
  abUseExperiments?: IAbUseExperiment[];
  isDealRentPrepaymentExperimentActive?: boolean;
}

export interface IOffersDispatchProps extends IButtonsAPI {
  closePopup(): void;
  onAgentRatingChanged(offer: IOffer, rating: TAgentRating): void;
  onComplaintSent(offerId: number, name: string, message?: string): void;
  onFavoriteChanged(offer: IOffer, isFavorite: boolean): void;
  onOfferCommentsChanged(offer: IOffer, commentOffer: string | undefined, commentAgent: string | undefined): void;
  onUserInfoPopupOpen(offerId: number): void;
  onUserInfoPopupClose(offerId: number): void;
  hideOfferAction(parameters: IHidingOfferInfo): void;
  sendComplaintFeedback(body: IComplaintFeedbackBody): void;
  statusChanger(status: EFeedbackComplaint): void;
  onAnalyticsButtonClick?(offerId: number, params: ICommercialTrackingOfferParams): void;
  onOfferCardClick?(): void;
}

export type TOffersProps = IOffersStateProps & IOffersDispatchProps;

function getOffersWithoutFavoritesFlag(offers: IOffer[]): IOffer[] {
  return offers.map(offer => ({ ...offer, isFavorite: null }));
}

export class Offers extends React.Component<TOffersProps, {}> {
  // Не обновляем выдачу, если не менялись фильтры или офферы
  public shouldComponentUpdate(nextProps: TOffersProps, nextState: {}) {
    const currentOffersWithoutFavoritesFlag = getOffersWithoutFavoritesFlag(this.props.offers);
    const nextOffersWithourFavoritesFlag = getOffersWithoutFavoritesFlag(nextProps.offers);

    return (
      !isEqual(this.props.jsonQuery, nextProps.jsonQuery) ||
      !isEqual(currentOffersWithoutFavoritesFlag, nextOffersWithourFavoritesFlag) ||
      !isEqual(this.props.commentingBlockedAgents, nextProps.commentingBlockedAgents) ||
      !isEqual(this.props.commentingBlockedOffers, nextProps.commentingBlockedOffers) ||
      !isEqual(this.props.commentingErroneousAgents, nextProps.commentingErroneousAgents) ||
      !isEqual(this.props.commentingErroneousOffers, nextProps.commentingErroneousOffers) ||
      !isEqual(this.props.hideOffer, nextProps.hideOffer) ||
      this.props.shownId !== nextProps.shownId ||
      this.props.complaintsFormStatus !== nextProps.complaintsFormStatus ||
      this.props.isPopupsOpened() !== nextProps.isPopupsOpened()
    );
  }

  public render() {
    const {
      offers,
      offersPerPage,
      currentPageNumber,
      isPrintEnabled,
      jsonQuery,
      offersQty,
      queryString,
      user,
      showPushNotification,
      hideOffer: { hiddenOffers, hiddenSuggestions },
      experiments,
      mlRankingGuid,
      showEmptyKpSearch,
      isLightView,
      isPromotionsInOffersActive,
      soprApi,
      deviceType,
      villagesLink,
      showCountrysideTrap,
      aggregatedOffersCount,
      extendedOffersCount,
      isQaMode,
      newbuildingConsultBannerAvailable,
      isCianAgencyExperimentActive,
    } = this.props;

    const phone = user.isAuthenticated && user.phones && user.phones.find(phone => phone.confirmed);
    const email = user.isAuthenticated && user.email;

    const { price } = jsonQuery;

    const pricesSorted = offers.map(getOfferPrice).sort((p1, p2) => p1 - p2);
    /**
     * Отправляем кастомный puid6, только если
     * пользователь задал фильтр цены "От"
     */
    const priceRange =
      price && price.value.gte ? getPriceRangeForPuidAdFox(pricesSorted[0], pricesSorted[pricesSorted.length - 1]) : '';

    const offerType = offerTypeFromJsonQuery(jsonQuery);
    const offerCardCreatorProps = {
      ...this.props,
      offersQty,
      queryString,
      priceRange,
      defaultPhone: phone && phone.phone,
      defaultEmail: email,
      showPushNotification,
      isLightView,
      isPromotionsInOffersActive,
      soprApi,
      deviceType,
      isCianAgencyExperimentActive,
    };

    const cardsWithAd = integrateAd({
      currentPageNumber,
      perPage: offersPerPage,
      offers,
      jsonQuery,
      offerCardCreator: getOfferCardCreator({ props: offerCardCreatorProps as IOfferCardCreatorProps }),
      isPrintEnabled,
      makeRequest: this.props.makeRequest,
      locationId: jsonQuery.region ? jsonQuery.region.value[0] : undefined,
      isCommercial: isCommercial(offerType),
      suggestionFragment: { isSimilar: false },
      priceRange,
      hiddenOffers,
      hiddenSuggestions,
      experiments,
      mlRankingGuid,
      isAgent: user.isAuthenticated && user.isAgent,
      villagesLink,
      showCountrysideTrap,
      extendedOffersOffset: aggregatedOffersCount - extendedOffersCount,
      keyPrefix: 'regular',
      isQaMode,
      newbuildingConsultBannerAvailable,
      realtyUserId: user.isAuthenticated ? user.userId : undefined,
    });

    const emptyListingBanner = showEmptyKpSearch ? (
      <EmptyKpSearchContainer />
    ) : (
      <PreInfiniteBanner type={EPreInfiniteBannerTypes.empty} />
    );

    return (
      <div className={style['wrapper']}>
        {!offers.length && emptyListingBanner}
        <AuctionStatusNotification />
        {cardsWithAd}
      </div>
    );
  }
}
